import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  ChangeEvent,
  createContext,
  useCallback
} from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import ConnectorTableActionDropdownItems from './ConnectorTableActionDropdown';
import ConnectorEditForm from './ConnectorEditForm';
import SearchBox from 'components/common/SearchBox';
import useAdvanceTable from 'hooks/common/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Connector } from 'models/Connector';
import { ConnectorGroup } from 'models/ConnectorGroup';
import PhoenixDocCard from 'components/base/PhoenixDocCard';
import { Alert, Button, Dropdown, Overlay, Tooltip } from 'react-bootstrap';
import ConnectorAddForm from './ConnectorAddForm';
import WidgetsSectionTitle from 'components/base/WidgetsSectionTitle';
import useCarrierConnectionConfig from 'hooks/useCarrierConnectionConfig';
import {
  CONNECTOR_SYNC_STATUS_FILTER_KEYS,
  getConnectorSyncStatusDotClass,
  getConnectorSyncStatusKey,
  getConnectorSyncStatusLabel,
  getConnectorStatusMessage
} from 'utils/connectorSyncStatus';

export type ConnectorContextType = {
  handleEdit: (connector: Connector) => void;
  handleDelete: (id: number) => void;
};

export const ConnectorContext = createContext<ConnectorContextType | null>(null);

const carrierLogos = (require as any).context('./carrier-logos', false, /\.(png|jpe?g|svg)$/);

const getCarrierLogo = (carrier?: string) => {
  if (!carrier) return null;
  const normalized = carrier.trim().toLowerCase();
  if (!normalized) return null;
  try {
    return carrierLogos(`./${normalized}.png`);
  } catch {
    return null;
  }
};

/** Ref + Overlay avoids Popper hiding the tooltip when the table reference is “clipped” by scroll parents. */
const ConnectorStatusCellWithTooltip: React.FC<{ connector: Connector }> = ({
  connector
}) => {
  const target = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHide = useCallback(() => {
    if (hideTimer.current !== null) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  const openTooltip = useCallback(() => {
    cancelHide();
    setShow(true);
  }, [cancelHide]);

  const closeTooltipSoon = useCallback(() => {
    cancelHide();
    hideTimer.current = setTimeout(() => {
      setShow(false);
      hideTimer.current = null;
    }, 120);
  }, [cancelHide]);

  useEffect(() => () => cancelHide(), [cancelHide]);

  const statusKey = getConnectorSyncStatusKey(connector);
  const label = getConnectorSyncStatusLabel(statusKey);
  const dotClass = getConnectorSyncStatusDotClass(statusKey);
  const statusMessage = getConnectorStatusMessage(connector);

  const dot = (
    <span
      ref={target}
      className={`rounded-circle d-inline-block ${dotClass}`}
      style={{ width: 8, height: 8 }}
      aria-hidden
    />
  );

  if (!statusMessage) {
    return (
      <div className="d-flex align-items-center gap-2">
        {dot}
        <span>{label}</span>
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex align-items-center gap-2"
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltipSoon}
      >
        {dot}
        <span>{label}</span>
      </div>
      <Overlay
        target={target}
        show={show}
        placement="top"
        container={typeof document !== 'undefined' ? document.body : undefined}
        transition={false}
        popperConfig={{
          strategy: 'fixed',
          modifiers: [{ name: 'hide', enabled: false }]
        }}
      >
        {overlayProps => (
          <Tooltip
            id={`connector-status-msg-${connector.id}`}
            {...overlayProps}
            className={[overlayProps.className, 'text-start'].filter(Boolean).join(' ')}
            style={{
              ...overlayProps.style,
              maxWidth: 'min(22rem, 90vw)',
              whiteSpace: 'normal'
            }}
            onMouseEnter={openTooltip}
            onMouseLeave={closeTooltipSoon}
          >
            {statusMessage}
          </Tooltip>
        )}
      </Overlay>
    </>
  );
};

type ConnectorManagementPanelProps = {
  connectorGroupId?: number;
  connectors: Connector[];
  createConnector: (newConnector: Connector) => Promise<void>;
  updateConnector: (id: number, updatedConnector: Connector) => Promise<void>;
  deleteConnector: (id: number) => Promise<void>;
  connectorGroups?: ConnectorGroup[];
};

const ConnectorManagementPanel: React.FC<ConnectorManagementPanelProps> = ({ connectorGroupId, connectors, createConnector, updateConnector, deleteConnector, connectorGroups }) => {
  const { resolveDisplayName } = useCarrierConnectionConfig();
  const [showAddForm, setShowAddForm] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [editingConnector, setEditingConnector] = useState<Connector | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [selectedCarriers, setSelectedCarriers] = useState<string[]>([]);
  const [selectedEnabledStatus, setSelectedEnabledStatus] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  
  const handleAddConnector = async (newConnector: Connector) => {
    try {
      await createConnector(newConnector);
      setNotification(`One connector added: ${newConnector.username}`);
      setTimeout(() => setNotification(null), 6000);
    } catch (error) {
      console.error('There was a problem with the add operation:', error);
    }
  };

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleEdit = (connector: Connector) => {
    setEditingConnector(connector);
  };

  const handleSave = () => {
    setEditingConnector(null);
  };

  const handleCancel = () => {
    setEditingConnector(null);
  };

  const handleDelete = async (id: number) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this Row?");
    if (confirmDelete) {
      await deleteConnector(id);
    }
  };

  // Extract unique carriers from connectors
  const carriers = useMemo(() => {
    const carrierSet = new Set<string>();
    connectors.forEach(connector => {
      if (connector.carrier && connector.carrier.trim() !== '') {
        carrierSet.add(connector.carrier);
      }
    });
    return Array.from(carrierSet).sort();
  }, [connectors]);

  const syncStatusFilterKeys = CONNECTOR_SYNC_STATUS_FILTER_KEYS;

  // Enabled status options
  const enabledStatuses = ['enabled', 'disabled'];

  const columns: ColumnDef<Connector>[] = useMemo(() => [
    {
      accessorKey: 'id',
      header: 'ID',
    },
    {
      accessorKey: 'carrier',
      header: 'Carrier',
      enableColumnFilter: true,
      filterFn: (row, columnId, value) => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return true;
        }
        const rowValue = row.getValue<string | undefined>(columnId);
        if (!rowValue) {
          return false;
        }
        return Array.isArray(value) && value.includes(rowValue);
      },
      cell: ({ row }) => {
        const carrier = row.original.carrier;
        const carrierLabel = resolveDisplayName(carrier);
        const logoSrc = getCarrierLogo(carrier);
        return (
          <div className="d-flex align-items-center gap-2">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={`${carrierLabel} logo`}
                style={{ width: 20, height: 20, objectFit: 'contain' }}
              />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center bg-200 text-900"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600
                }}
                aria-label="Unknown carrier logo"
                title="Unknown carrier"
              >
                {carrierLabel?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <span>{carrierLabel}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'connector_group_name',
      header: 'group',
      cell: ({ row }) => {
        const group = row.original.connector_group_name;
        return <span>{group ?? '—'}</span>;
      },
    },
    {
      accessorKey: 'username',
      header: 'UserName',
    },
    {
      accessorKey: 'enabled',
      header: 'Enabled',
      enableColumnFilter: true,
      filterFn: (row, columnId, value) => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return true;
        }
        const rowValue = row.getValue<boolean | undefined>(columnId);
        const isEnabled = Boolean(rowValue);
        return Array.isArray(value) && value.includes(isEnabled ? 'enabled' : 'disabled');
      },
      cell: ({ row }) => {
        const isEnabled = Boolean(row.original.enabled);
        return (
          <div className="d-flex align-items-center gap-2">
            <span
              className={`rounded-circle d-inline-block ${isEnabled ? 'bg-success' : 'bg-secondary'}`}
              style={{ width: 8, height: 8 }}
            />
            <span>{isEnabled ? 'Enabled' : 'Disabled'}</span>
          </div>
        );
      }
    },
    // {
    //   accessorKey: 'password',
    //   header: 'Password',
    // },
    {
      id: 'status',
      accessorFn: row => getConnectorSyncStatusKey(row),
      header: 'Status',
      enableColumnFilter: true,
      filterFn: (row, _columnId, value) => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return true;
        }
        if (!row.original.enabled) {
          return false;
        }
        const key = getConnectorSyncStatusKey(row.original);
        return Array.isArray(value) && value.includes(key);
      },
      cell: ({ row }) => {
        if (!row.original.enabled) {
          return null;
        }
        return <ConnectorStatusCellWithTooltip connector={row.original} />;
      },
    },
    // {
    //   accessorKey: 'remote_directory',
    //   header: 'Remote Directory',
    // },
    // {
    //   accessorKey: 'endpoint',
    //   header: 'Endpoint',
    // },
    // {
    //   accessorKey: 'api_key',
    //   header: 'Api Key',
    // },
    {
      id: 'action',
      header: 'Actions',
      cell: ({ row }) => (
        <RevealDropdownTrigger className="d-flex justify-content-end w-100">
          <RevealDropdown>
            <ConnectorTableActionDropdownItems connector={row.original} />
          </RevealDropdown>
        </RevealDropdownTrigger>
      ),
      meta: {
        headerProps: { style: { width: '7%' } },
        cellProps: { className: 'text-end' },
      },
    },
  ], [resolveDisplayName]);

  const table = useAdvanceTable({
    data: connectors,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
  });

  const { getColumn } = table;

  const clearAllFilters = useCallback(() => {
    ['carrier', 'enabled', 'status'].forEach(
      columnId => {
        getColumn(columnId)?.setFilterValue([]);
      }
    );
    setSelectedCarriers([]);
    setSelectedEnabledStatus([]);
    setSelectedStatuses([]);
  }, [getColumn]);

  const getDataCount = useCallback(
    (columnId: string, value: string) => {
      if (columnId === 'carrier') {
        return connectors.filter(connector => connector.carrier === value).length;
      }
      if (columnId === 'enabled') {
        return connectors.filter(connector => {
          const isEnabled = Boolean(connector.enabled);
          return (isEnabled ? 'enabled' : 'disabled') === value;
        }).length;
      }
      if (columnId === 'status') {
        return connectors.filter(connector => {
          if (!connector.enabled) {
            return false;
          }
          return getConnectorSyncStatusKey(connector) === value;
        }).length;
      }
      return 0;
    },
    [connectors]
  );

  const handleCarrierToggle = useCallback((carrier: string) => {
    setSelectedCarriers(prev => {
      const newSelection = prev.includes(carrier)
        ? prev.filter(c => c !== carrier)
        : [...prev, carrier];

      getColumn('carrier')?.setFilterValue(newSelection.length > 0 ? newSelection : []);
      return newSelection;
    });
  }, [getColumn]);

  const handleSelectAllCarriers = useCallback(() => {
    if (selectedCarriers.length === carriers.length) {
      // Deselect all
      setSelectedCarriers([]);
      getColumn('carrier')?.setFilterValue([]);
    } else {
      // Select all
      setSelectedCarriers(carriers);
      getColumn('carrier')?.setFilterValue(carriers);
    }
  }, [selectedCarriers.length, carriers, getColumn]);

  const handleEnabledStatusToggle = useCallback((status: string) => {
    setSelectedEnabledStatus(prev => {
      const newSelection = prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status];

      getColumn('enabled')?.setFilterValue(newSelection.length > 0 ? newSelection : []);
      return newSelection;
    });
  }, [getColumn]);

  const handleSelectAllEnabledStatuses = useCallback(() => {
    if (selectedEnabledStatus.length === enabledStatuses.length) {
      // Deselect all
      setSelectedEnabledStatus([]);
      getColumn('enabled')?.setFilterValue([]);
    } else {
      // Select all
      setSelectedEnabledStatus(enabledStatuses);
      getColumn('enabled')?.setFilterValue(enabledStatuses);
    }
  }, [selectedEnabledStatus.length, getColumn]);

  const handleStatusToggle = useCallback((status: string) => {
    setSelectedStatuses(prev => {
      const newSelection = prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status];

      getColumn('status')?.setFilterValue(newSelection.length > 0 ? newSelection : []);
      return newSelection;
    });
  }, [getColumn]);

  const handleSelectAllStatuses = useCallback(() => {
    if (selectedStatuses.length === syncStatusFilterKeys.length) {
      // Deselect all
      setSelectedStatuses([]);
      getColumn('status')?.setFilterValue([]);
    } else {
      // Select all
      setSelectedStatuses([...syncStatusFilterKeys]);
      getColumn('status')?.setFilterValue([...syncStatusFilterKeys]);
    }
  }, [selectedStatuses.length, syncStatusFilterKeys, getColumn]);

  const carrierLabel = selectedCarriers.length === 0
    ? 'All Carriers'
    : selectedCarriers.length === 1
      ? resolveDisplayName(selectedCarriers[0])
      : `${selectedCarriers.length} Carriers Selected`;

  const enabledStatusLabel = selectedEnabledStatus.length === 0
    ? 'All Activation Statuses'
    : selectedEnabledStatus.length === 1
      ? selectedEnabledStatus[0].charAt(0).toUpperCase() + selectedEnabledStatus[0].slice(1)
      : `${selectedEnabledStatus.length} Statuses Selected`;

  const statusLabel = selectedStatuses.length === 0
    ? 'All Processing Statuses'
    : selectedStatuses.length === 1
      ? getConnectorSyncStatusLabel(
          selectedStatuses[0] as (typeof CONNECTOR_SYNC_STATUS_FILTER_KEYS)[number]
        )
      : `${selectedStatuses.length} Statuses Selected`;

  const contextValue = {
    handleEdit,
    handleDelete,
  };

  return (
    <ConnectorContext.Provider value={contextValue}>
      <div>
        <PhoenixDocCard className="mb-4">
          {/* <WidgetsSectionTitle
        title="Connectors"
        subtitle=""
        // icon={faPercentage}
        className="my-5"
      /> */}
          <PhoenixDocCard.Body>
            <div className="mb-4">
              <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-3 mb-3">
                <Button
                  className="addBtn w-100 w-md-auto text-nowrap"
                  variant="primary"
                  onClick={() => setShowAddForm(true)}
                >
                  + Add Connector
                </Button>

                <SearchBox
                  placeholder="Search..."
                  size="sm"
                  onChange={handleSearchInputChange}
                  className="w-100 w-md-auto ms-md-auto"
                />
              </div>
              <hr className="my-3 border-200" />

              {/* Filter Section */}
              <div className="d-flex flex-column flex-sm-row flex-wrap align-items-stretch align-items-sm-center gap-2">
                {/* Enabled Status Filter */}
                <div className="w-100 w-sm-auto">
                  <Dropdown className="position-static w-100">
                    <Dropdown.Toggle
                      variant="phoenix-secondary"
                      className="px-4 dropdown-caret-none w-100 text-start text-sm-center"
                    >
                      {enabledStatusLabel}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="py-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <Dropdown.Item
                        onClick={handleSelectAllEnabledStatuses}
                        className="d-flex align-items-center"
                      >
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={selectedEnabledStatus.length === enabledStatuses.length && enabledStatuses.length > 0}
                          onChange={handleSelectAllEnabledStatuses}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="fw-semibold">Select All</span>
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      {enabledStatuses.map(status => {
                        const count = getDataCount('enabled', status);
                        const isSelected = selectedEnabledStatus.includes(status);
                        return (
                          <Dropdown.Item
                            key={status}
                            onClick={() => handleEnabledStatusToggle(status)}
                            className="d-flex justify-content-between align-items-center"
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex align-items-center">
                              <input
                                type="checkbox"
                                className="form-check-input me-2"
                                checked={isSelected}
                                onChange={() => handleEnabledStatusToggle(status)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                            </div>
                            <span className="text-700 fw-semi-bold ms-3">({count})</span>
                          </Dropdown.Item>
                        );
                      })}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {/* Status Filter */}
                <div className="w-100 w-sm-auto">
                  <Dropdown className="position-static w-100">
                    <Dropdown.Toggle
                      variant="phoenix-secondary"
                      className="px-4 dropdown-caret-none w-100 text-start text-sm-center"
                    >
                      {statusLabel}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="py-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <Dropdown.Item
                        onClick={handleSelectAllStatuses}
                        className="d-flex align-items-center"
                      >
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={
                            selectedStatuses.length === syncStatusFilterKeys.length &&
                            syncStatusFilterKeys.length > 0
                          }
                          onChange={handleSelectAllStatuses}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="fw-semibold">Select All</span>
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      {syncStatusFilterKeys.map(statusKey => {
                        const count = getDataCount('status', statusKey);
                        const isSelected = selectedStatuses.includes(statusKey);
                        return (
                          <Dropdown.Item
                            key={statusKey}
                            onClick={() => handleStatusToggle(statusKey)}
                            className="d-flex justify-content-between align-items-center"
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex align-items-center">
                              <input
                                type="checkbox"
                                className="form-check-input me-2"
                                checked={isSelected}
                                onChange={() => handleStatusToggle(statusKey)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span>{getConnectorSyncStatusLabel(statusKey)}</span>
                            </div>
                            <span className="text-700 fw-semi-bold ms-3">({count})</span>
                          </Dropdown.Item>
                        );
                      })}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {/* Carrier Filter */}
                <div className="w-100 w-sm-auto">
                  <Dropdown className="position-static w-100">
                    <Dropdown.Toggle
                      variant="phoenix-secondary"
                      className="px-4 dropdown-caret-none w-100 text-start text-sm-center"
                    >
                      {carrierLabel}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="py-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <Dropdown.Item
                        onClick={handleSelectAllCarriers}
                        className="d-flex align-items-center"
                      >
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={selectedCarriers.length === carriers.length && carriers.length > 0}
                          onChange={handleSelectAllCarriers}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="fw-semibold">Select All</span>
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      {carriers.map(carrier => {
                        const count = getDataCount('carrier', carrier);
                        const isSelected = selectedCarriers.includes(carrier);
                        return (
                          <Dropdown.Item
                            key={carrier}
                            onClick={() => handleCarrierToggle(carrier)}
                            className="d-flex justify-content-between align-items-center"
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex align-items-center">
                              <input
                                type="checkbox"
                                className="form-check-input me-2"
                                checked={isSelected}
                                onChange={() => handleCarrierToggle(carrier)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span>{resolveDisplayName(carrier)}</span>
                            </div>
                            <span className="text-700 fw-semi-bold ms-3">({count})</span>
                          </Dropdown.Item>
                        );
                      })}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {/* Clear All Filters Button */}
                <div className="w-100 w-sm-auto">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger w-100"
                    onClick={clearAllFilters}
                    disabled={selectedCarriers.length === 0 && selectedEnabledStatus.length === 0 && selectedStatuses.length === 0}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>

            {notification && (
              <Alert
                variant="outline-success"
                onClose={() => setNotification(null)}
                dismissible
              >
                {notification}
              </Alert>
            )}
          <ConnectorAddForm
            key={formKey} // مقدار جدید هر بار فرم را ریست می‌کند
            show={showAddForm}
            handleClose={() => {
              setShowAddForm(false);
              setFormKey(prevKey => prevKey + 1); // مقدار جدیدی بده که فرم ریست شود
            }}
            handleSave={(newConnector) => {
              handleAddConnector(newConnector);
              setShowAddForm(false);
              setFormKey(prevKey => prevKey + 1); // مقدار جدیدی بده که فرم ریست شود
            }}
            connectorGroupId={connectorGroupId} 
            connectorGroups={connectorGroups}
          />

            <AdvanceTableProvider {...table}>
              <AdvanceTable
                tableProps={{
                  size: 'sm',
                  className: 'phoenix-table fs-9 mb-0 border-top border-200',
                }}
                rowClassName="hover-actions-trigger position-static"
              />
              <AdvanceTableFooter navBtn />
            </AdvanceTableProvider>
            {editingConnector && (
              <ConnectorEditForm
                connector={editingConnector}
                onSave={handleSave}
                onCancel={handleCancel}
                handleUpdate={updateConnector}
              />
            )}


          </PhoenixDocCard.Body>
        </PhoenixDocCard>
      </div>
    </ConnectorContext.Provider>
  );
};

export default ConnectorManagementPanel;
